import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ClipboardCheck, ArrowLeft, ArrowRight, CheckCircle, 
  Star, Send, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import confetti from 'canvas-confetti';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WeeklySurveyPage = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [myResponses, setMyResponses] = useState([]);

  useEffect(() => {
    fetchSurvey();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSurvey = async () => {
    try {
      const token = localStorage.getItem('token');
      const [surveyRes, responsesRes] = await Promise.all([
        fetch(`${API_URL}/api/surveys/weekly`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/surveys/my-responses`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (surveyRes.ok) {
        setSurvey(await surveyRes.json());
      }
      if (responsesRes.ok) {
        const data = await responsesRes.json();
        setMyResponses(data);
        
        // Check if current week already submitted
        if (data.length > 0) {
          const currentWeek = Math.floor((Date.now() - new Date('2024-06-01').getTime()) / (1000 * 60 * 60 * 24 * 7)) + 1;
          if (data.some(r => r.week_number === currentWeek)) {
            setSubmitted(true);
          }
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const handleResponse = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const submitSurvey = async () => {
    const responseArray = Object.entries(responses).map(([questionId, answer]) => ({
      question_id: questionId,
      answer: answer
    }));

    try {
      const res = await fetch(`${API_URL}/api/surveys/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          survey_id: survey.id,
          responses: responseArray
        })
      });

      if (res.ok) {
        const data = await res.json();
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        toast.success(`Survey completed! +${data.points_earned} points 🎉`);
        setSubmitted(true);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit survey');
      }
    } catch (error) {
      toast.error('Failed to submit survey');
    }
  };

  const questions = survey?.questions || [];
  const progress = questions.length > 0 ? ((currentQuestion + 1) / questions.length) * 100 : 0;
  const currentQ = questions[currentQuestion];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-2xl font-heading font-bold text-primary">Loading...</div>
      </div>
    );
  }

  // Already submitted
  if (submitted) {
    return (
      <div className="min-h-screen pb-20" style={{ backgroundColor: colors.background }}>
        <div className="p-6 text-white rounded-b-3xl" style={{ background: colors.header_gradient || colors.header }}>
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-white hover:bg-white/20 rounded-full"
                data-testid="back-to-dashboard-completed"
              >
                <ArrowLeft className="w-6 h-6" />
              </Button>
              <div>
                <h1 className="font-heading text-3xl font-bold" style={{ color: "white" }}>Weekly Survey</h1>
                <p className="opacity-90">Week {survey?.week_number}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card className="p-8 text-center rounded-3xl">
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold mb-2">Survey Completed! 🎉</h2>
            <p className="text-muted-foreground mb-4">
              You've already submitted this week's survey. Thank you for your feedback!
            </p>
            <Button onClick={() => navigate('/dashboard')} className="rounded-full">
              Back to Dashboard
            </Button>
          </Card>

          {/* Previous Responses */}
          {myResponses.length > 0 && (
            <div className="mt-8">
              <h3 className="font-heading text-xl font-bold mb-4">Your Survey History</h3>
              <div className="space-y-3">
                {myResponses.map((resp) => (
                  <Card key={resp.id} className="p-4 rounded-2xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <Badge className="bg-blue-100 text-blue-800">Week {resp.week_number}</Badge>
                        <p className="text-sm text-muted-foreground mt-1">
                          Submitted on {new Date(resp.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">+{resp.points_earned} pts</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

        <Navigation role="youth" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-6 text-white rounded-b-3xl">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/20 rounded-full"
              data-testid="survey-back-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div className="flex-1">
              <h1 className="font-heading text-2xl font-bold">{survey?.title || 'Weekly Survey'}</h1>
              <p className="opacity-90">Question {currentQuestion + 1} of {questions.length}</p>
            </div>
          </div>

          <Progress value={progress} className="h-3 bg-white/30" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {currentQ && (
            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card className="p-6 rounded-3xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center font-bold text-blue-600">
                    {currentQuestion + 1}
                  </div>
                  <h2 className="font-heading text-xl font-bold">{currentQ.question}</h2>
                </div>

                {/* Rating Question */}
                {currentQ.type === 'rating' && (
                  <div className="flex justify-center gap-3 mb-6">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <motion.button
                        key={rating}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleResponse(currentQ.id, rating.toString())}
                        className={`w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold transition-all ${
                          responses[currentQ.id] === rating.toString()
                            ? 'bg-blue-500 text-white shadow-lg scale-110'
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        {rating}
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Multiple Choice Question */}
                {currentQ.type === 'multiple_choice' && (
                  <div className="space-y-3 mb-6">
                    {currentQ.options?.map((option) => (
                      <motion.button
                        key={option}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleResponse(currentQ.id, option)}
                        className={`w-full p-4 rounded-2xl text-left font-medium transition-all ${
                          responses[currentQ.id] === option
                            ? 'bg-blue-500 text-white shadow-lg'
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        {option}
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Text Question */}
                {currentQ.type === 'text' && (
                  <Textarea
                    value={responses[currentQ.id] || ''}
                    onChange={(e) => handleResponse(currentQ.id, e.target.value)}
                    placeholder="Type your response here..."
                    className="rounded-xl min-h-[120px] mb-6"
                  />
                )}

                {/* Rating Labels */}
                {currentQ.type === 'rating' && (
                  <div className="flex justify-between text-sm text-muted-foreground mb-6">
                    <span>Poor</span>
                    <span>Excellent</span>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentQuestion(prev => prev - 1)}
                    disabled={currentQuestion === 0}
                    className="flex-1 h-12 rounded-full"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Previous
                  </Button>

                  {currentQuestion < questions.length - 1 ? (
                    <Button
                      onClick={() => setCurrentQuestion(prev => prev + 1)}
                      disabled={!responses[currentQ?.id]}
                      className="flex-1 h-12 rounded-full bg-blue-500 hover:bg-blue-600"
                    >
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={submitSurvey}
                      disabled={!responses[currentQ?.id]}
                      className="flex-1 h-12 rounded-full bg-green-500 hover:bg-green-600"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Submit Survey
                    </Button>
                  )}
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Question Navigation */}
        <div className="flex justify-center gap-2 mt-6">
          {questions.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentQuestion(idx)}
              className={`w-3 h-3 rounded-full transition-all ${
                idx === currentQuestion 
                  ? 'bg-blue-500 w-8' 
                  : responses[questions[idx]?.id]
                    ? 'bg-green-500' 
                    : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      <Navigation role="youth" />
    </div>
  );
};

export default WeeklySurveyPage;
